import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, Check, X, Loader2 } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';

const Register = () => {
  useSEO({
    title: 'Register',
    description: 'Join Family Wishlist today. Create your secure account, set up or join family groups, and start sharing gift ideas safely and easily with those who matter most.',
    keywords: 'family wishlist register, sign up family wishlist, coordinate gifts, family wishlists',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      'name': 'Register - Family Wishlist',
      'description': 'User registration portal for Family Wishlist.',
      'url': `${window.location.origin}/register`
    }
  });

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState(''); // Honeypot field
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isOver13, setIsOver13] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [passwordMatchError, setPasswordMatchError] = useState(false);

  const { register } = useAuth();
  const navigate = useNavigate();

  const requirements = [
    { label: 'At least 8 characters', test: (p) => p.length >= 8 },
    { label: 'At least one uppercase letter', test: (p) => /[A-Z]/.test(p) },
    { label: 'At least one lowercase letter', test: (p) => /[a-z]/.test(p) },
    { label: 'At least one number', test: (p) => /\d/.test(p) },
    { label: 'At least one special character', test: (p) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(p) },
  ];

  useEffect(() => {
    if (confirmPassword && password !== confirmPassword) {
      setPasswordMatchError(true);
    } else {
      setPasswordMatchError(false);
    }
  }, [password, confirmPassword]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Check if all requirements are met
    const allMet = requirements.every(req => req.test(password));
    if (!allMet) {
      setError('Please ensure your password meets all requirements.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (!isOver13) {
      setError('You must be over 13 and agree to the consent statement to register.');
      return;
    }

    setLoading(true);
    try {
      await register(name, email, password, isOver13, website);
      navigate('/verify-email', { state: { email } });
    } catch (err) {
      setError(err.response?.data?.msg || 'Registration failed');
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto mt-20 card">
      <h2 className="text-3xl font-bold text-center text-xmas-red mb-6">Join the Family</h2>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium">Name</label>
          <input
            type="text"
            className="w-full p-2 border rounded mt-1"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>
        {/* Honeypot field - hidden from users but visible to bots */}
        <div style={{ position: 'absolute', left: '-9999px' }} aria-hidden="true">
          <label htmlFor="website">Website</label>
          <input
            id="website"
            type="text"
            name="website"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            tabIndex="-1"
            autoComplete="off"
          />
        </div>
        <div>
          <label className="block text-sm font-medium">Email</label>
          <input
            type="email"
            className="w-full p-2 border rounded mt-1"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium">Password</label>
          <div className="relative mt-1">
            <input
              type={showPassword ? 'text' : 'password'}
              className="w-full p-2 border rounded pr-10"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <button
              type="button"
              className="absolute right-2 top-2 text-gray-500"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>

          <div className="mt-2 space-y-1">
            {requirements.map((req, index) => {
              const met = req.test(password);
              return (
                <div key={index} className={`flex items-center text-xs ${met ? 'text-green-600' : 'text-gray-500'}`}>
                  {met ? <Check size={12} className="mr-1" /> : <X size={12} className="mr-1" />}
                  {req.label}
                </div>
              );
            })}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium">Confirm Password</label>
          <div className="relative mt-1">
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              className={`w-full p-2 border rounded pr-10 ${passwordMatchError ? 'border-red-500' : ''}`}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
            <button
              type="button"
              className="absolute right-2 top-2 text-gray-500"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
          {passwordMatchError && (
            <p className="text-red-500 text-xs mt-1">Passwords do not match.</p>
          )}
        </div>

        <div className="flex items-start gap-2 mt-4">
          <input
            type="checkbox"
            id="isOver13"
            className="mt-1 h-4 w-4 rounded border-gray-300 text-xmas-green focus:ring-xmas-green"
            checked={isOver13}
            onChange={(e) => setIsOver13(e.target.checked)}
            required
          />
          <label htmlFor="isOver13" className="text-sm text-gray-700 select-none">
            I explicitly consent and confirm that I am <strong>13 years of age or older</strong>.
          </label>
        </div>

        <div className="text-xs text-gray-500 text-center mt-2">
          By registering, you consent to our{' '}
          <Link to="/terms-of-service" className="text-xmas-red hover:underline font-semibold">
            Terms of Service
          </Link>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded relative">
            {error}
          </div>
        )}

        <button
          type="submit"
          className="btn-primary w-full disabled:opacity-50 disabled:bg-gray-400 disabled:cursor-not-allowed"
          disabled={loading || !isOver13}
        >
          {loading ? (
            <>
              <Loader2 className="animate-spin" size={20} />
              Registering...
            </>
          ) : (
            'Register'
          )}
        </button>
      </form>
      <p className="mt-4 text-center">
        Already have an account? <Link to="/login" className="text-xmas-green font-bold">Login</Link>
      </p>
    </div>
  );
};

export default Register;
